import CryptoJS from "crypto-js";
import { logger } from "../logger";

/**
 * Password-based key derivation for the stored trading credential.
 *
 * WHAT THIS PROTECTS: the signing key and API secret, encrypted at rest in
 * localStorage. An attacker with a copy of that record can brute-force the
 * password offline, so the iteration count is the entire cost of an attempt.
 *
 * WHY THERE ARE TWO CONSTANTS: records written before this change used 10,000
 * iterations and carry no record of it. They must keep decrypting at 10,000
 * forever, so `LEGACY_KDF_ITERATIONS` is the default when a stored record has
 * no `kdfIterations` field. Everything written from now on records the count it
 * used, which is what makes any future change a data migration rather than a
 * lockout.
 *
 * WHY 250,000 AND NOT OWASP'S 600,000: crypto-js is pure JavaScript and
 * synchronous, so this runs on the UI thread. Measured on this machine (Node
 * 24, a fast desktop — a phone is materially worse):
 *
 *     10,000 ->   54ms      250,000 -> 1,145ms
 *    100,000 ->  467ms      600,000 -> 2,704ms
 *
 * 600,000 would freeze the app for several seconds on every unlock. 250,000 is
 * crypto-js 4.2.0's OWN default — the value its maintainers moved to after
 * CVE-2023-46233 — and it is a 25x improvement on what this code was doing.
 *
 * TO GO FURTHER, change the primitive, not the number: `crypto.subtle`'s native
 * PBKDF2 does 600,000 in a fraction of the time and runs off the main thread.
 * That makes `encrypt`/`decrypt` async and touches all five call sites, so it
 * is a deliberate follow-up rather than a drive-by.
 *
 * NOTE: AES-CBC here is unauthenticated (no MAC), so ciphertext is malleable.
 * That matters less for a local record whose plaintext is validated after
 * decryption (see `decryptEncryptedAccountInfo`), but AES-GCM would be the
 * right choice in the same WebCrypto migration.
 */

/** Iteration count for records written before the count was recorded. */
export const LEGACY_KDF_ITERATIONS = 10000;

/** Iteration count for every record written from now on. */
export const CURRENT_KDF_ITERATIONS = 250000;

export function encrypt(
  text: string,
  password: string,
  iterations: number = CURRENT_KDF_ITERATIONS
): {
  encryptedText: string;
  iv: string;
  salt: string;
  iterations: number;
} {
  try {
    // Random salt for key derivation
    const salt = CryptoJS.lib.WordArray.random(128 / 8);
    const iv = CryptoJS.lib.WordArray.random(128 / 8);

    // Deriving key using PBKDF2
    const key = CryptoJS.PBKDF2(password, salt, {
      keySize: 256 / 32, // for AES-256
      iterations,
    });

    // Encrypting the text using the derived key
    const encrypted = CryptoJS.AES.encrypt(text, key, {
      // Random IV
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    // const encryptedText = CryptoJS.AES.encrypt(text, password).toString();
    return {
      encryptedText: encrypted.toString(),
      iv: iv.toString(),
      salt: salt.toString(),
      // Returned so the caller can persist it. A record that does not say how
      // it was derived can never be re-derived differently.
      iterations,
    };
  } catch (e) {
    logger.error("Error during encryption", e);
    throw e;
  }
}

// Dont need to catch error for decryption because we just assume the password is wrong
export function decrypt(
  encryptedText: string,
  password: string,
  ivString: string,
  saltString: string,
  /**
   * Defaults to the LEGACY count on purpose: a stored record without a
   * `kdfIterations` field predates the field, and was therefore written at
   * 10,000. Defaulting to the current value here would lock every existing
   * user out of their credential.
   */
  iterations: number = LEGACY_KDF_ITERATIONS
) {
  // Assuming these values were saved from the encryption process
  const iv = CryptoJS.enc.Hex.parse(ivString); // The IV as a hex string
  const salt = CryptoJS.enc.Hex.parse(saltString); // The salt as a hex string

  // Deriving the same key using PBKDF2, salt and password must be the same
  const key = CryptoJS.PBKDF2(password, salt, {
    keySize: 256 / 32, // Must be the same as during encryption
    iterations, // Must be the same as during encryption
  });

  // Decrypting the text using the derived key
  const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  const originalText = decrypted.toString(CryptoJS.enc.Utf8);
  return originalText;
}
